import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, Menu, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAudio } from "../context/AudioContext";

const navIds = [
  { id: "story", key: "story" },
  { id: "family", key: "family" },
  { id: "program", key: "program" },
  { id: "venue", key: "venue" },
  { id: "dresscode", key: "dressCode" },
  { id: "rsvp", key: "rsvp" },
];

export default function Header() {
  const { lang, setLang, t } = useLang();
  const { isPlaying, toggle } = useAudio();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goto = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.4 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-ivory/85 border-b border-wedding-border/40"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between py-4 md:py-5">
        {/* Logo */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-1 group"
          data-testid="logo-button"
        >
          <span className="font-script text-4xl text-burgundy leading-none group-hover:text-wine transition-colors">
            N
          </span>
          <span className="font-serif text-sm tracking-[0.3em] text-ink/70 mx-1">
            &
          </span>
          <span className="font-script text-4xl text-burgundy leading-none group-hover:text-wine transition-colors">
            R
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navIds.map((n) => (
            <button
              key={n.id}
              onClick={() => goto(n.id)}
              className="text-xs uppercase tracking-[0.25em] text-ink/70 hover:text-burgundy transition-colors"
              data-testid={`nav-${n.id}-btn`}
            >
              {t.nav[n.key]}
            </button>
          ))}
        </nav>

        {/* Controls */}
        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <div
            className="flex items-center text-xs tracking-[0.2em] border border-wedding-border rounded-full overflow-hidden"
            data-testid="language-switcher"
          >
            {[
              { code: "ro", label: "RO" },
              { code: "it", label: "IT" },
              { code: "de", label: "DE" },
            ].map(({ code, label }) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                className={`px-2.5 sm:px-3 py-1.5 transition-colors ${
                  lang === code
                    ? "bg-burgundy text-ivory"
                    : "text-ink/70 hover:bg-cream"
                }`}
                data-testid={`lang-${code}-button`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Audio toggle */}
          <button
            onClick={toggle}
            aria-label={isPlaying ? t.audio.pause : t.audio.play}
            title={isPlaying ? t.audio.pause : t.audio.play}
            className="w-10 h-10 rounded-full border border-wedding-border flex items-center justify-center text-ink/70 hover:border-burgundy hover:text-burgundy transition-colors music-toggle"
            data-testid="audio-toggle-button"
          >
            {isPlaying ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>

          {/* Mobile menu */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-full border border-wedding-border flex items-center justify-center text-ink/70 hover:border-burgundy hover:text-burgundy transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden border-t border-wedding-border/40 bg-ivory/95 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navIds.map((n) => (
                <button
                  key={n.id}
                  onClick={() => goto(n.id)}
                  className="text-left text-xs uppercase tracking-[0.25em] text-ink/80 hover:text-burgundy transition-colors py-2"
                  data-testid={`nav-mobile-${n.id}-btn`}
                >
                  {t.nav[n.key]}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
